import AuthService from "./AuthService";

/**
 * @namespace com.ang.portal.service
 */

export interface GroupSummary {
	id: number;
	name: string;
}

export interface GroupRecord {
	id: number;
	name: string;
	description: string;
	/** Assigned to this group directly. */
	roles: string[];
	permissions: string[];
	memberCount: number;
	includedGroupCount: number;
	/** Resolved across the groups this one includes. Read-only — the server derives them. */
	effectiveRoles: string[];
	effectivePermissions: string[];
	effectiveMemberCount: number;
}

export interface GroupDetail {
	id: number;
	name: string;
	description: string;
	/** This group's own assignments — what the edit dialog round-trips. */
	roles: string[];
	permissions: string[];
	members: Array<{ id: number; email: string; name: string }>;
	variables: Array<{ key: string; value: string }>;
	includedGroups: GroupSummary[];
	/** Resolved across the nesting. Read-only — sending these back would flatten it. */
	effectiveRoles: string[];
	effectivePermissions: string[];
	effectiveMemberCount: number;
}

export interface GroupLookupResponse {
	roles: string[];
	permissions: string[];
	users: Array<{ id: number; email: string; name: string }>;
	groups: GroupSummary[];
}

export interface CreateGroupPayload {
	name: string;
	description?: string;
	roles: string[];
	permissions: string[];
	memberIds: number[];
	variables: Array<{ key: string; value: string }>;
	includedGroupIds: number[];
}

export interface UpdateGroupPayload {
	name: string;
	description?: string;
	roles: string[];
	permissions: string[];
	memberIds: number[];
	variables: Array<{ key: string; value: string }>;
	includedGroupIds: number[];
}

async function parseError(response: Response, fallbackMessage: string): Promise<Error> {
	const error = await response.json().catch(() => ({ message: fallbackMessage })) as { message?: string };
	return new Error(error.message || fallbackMessage);
}

const GroupsService = {
	async getGroups(): Promise<GroupRecord[]> {
		const response = await AuthService.authenticatedFetch("/api/groups");
		if (!response.ok) {
			throw await parseError(response, "Failed to fetch groups");
		}
		return response.json() as Promise<GroupRecord[]>;
	},

	async getGroup(groupId: number): Promise<GroupDetail> {
		const response = await AuthService.authenticatedFetch(`/api/groups/${groupId}`);
		if (!response.ok) {
			throw await parseError(response, "Failed to fetch group");
		}
		return response.json() as Promise<GroupDetail>;
	},

	async getLookups(): Promise<GroupLookupResponse> {
		const response = await AuthService.authenticatedFetch("/api/groups/lookups");
		if (!response.ok) {
			throw await parseError(response, "Failed to fetch lookup data");
		}
		return response.json() as Promise<GroupLookupResponse>;
	},

	async createGroup(payload: CreateGroupPayload): Promise<GroupDetail> {
		const response = await AuthService.authenticatedFetch("/api/groups", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			throw await parseError(response, "Failed to create group");
		}
		return response.json() as Promise<GroupDetail>;
	},

	async updateGroup(groupId: number, payload: UpdateGroupPayload): Promise<GroupDetail> {
		const response = await AuthService.authenticatedFetch(`/api/groups/${groupId}`, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload)
		});
		if (!response.ok) {
			throw await parseError(response, "Failed to update group");
		}
		return response.json() as Promise<GroupDetail>;
	},

	async deleteGroup(groupId: number): Promise<void> {
		const response = await AuthService.authenticatedFetch(`/api/groups/${groupId}`, {
			method: "DELETE"
		});
		if (!response.ok) {
			throw await parseError(response, "Failed to delete group");
		}
	}
};

export default GroupsService;
